import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams, useHistory } from 'react-router-dom';
import { catalogs } from 'store';
import { checkAndRedirectCatalog } from 'utils';
import {
    defaultVmDataDisk,
    environmentDetails,
    isConfigDiskFulfilled,
    isMandatoryTagsFullfilled,
    orderId,
    quickConfigDetails,
    sspSubmitOrder,
    toggleSspModels,
} from 'pages/provisioning/store/oracle';
import ProvisioningStyles from 'pages/provisioning/oracle/style';
import { formOrderObjFromCart, formOrderObjFromOrderDetails, resetOrder, restEnv } from './utils';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/oracle/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/oracle/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/oracle/SspForm'));
const Tags = React.lazy(() => import('pages/provisioning/oracle/Tags'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/oracle/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/oracle/UpdatedToCartConfirmModal'));
const ConfirmDD = React.lazy(() => import('pages/provisioning/oracle/ConfirmDD'));
const SamRequest = React.lazy(() => import('pages/provisioning/oracle/sam-request'));
const QuickConfig = React.lazy(() => import('pages/provisioning/oracle/QuickConfig'));
const CurrentServerDetails = React.lazy(() => import('pages/provisioning/oracle/components/CurrentServerDetails'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/oracle/ShowCostDetails'));
const NVMeDialogModal = React.lazy(() => import('pages/provisioning/oracle/NVMeDialogModal'));

const OracleProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [vmDataDisk, setVmDataDisk] = useRecoilState(defaultVmDataDisk);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetEnvDetails] = useRecoilState(environmentDetails);
    const [, resetIsMandatoryTagsFullfilled] = useRecoilState(isMandatoryTagsFullfilled);
    const [, resetIsConfigDiskFulfilled] = useRecoilState(isConfigDiskFulfilled);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetQuickConfig] = useRecoilState(quickConfigDetails);
    const { osType, serverType, catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const history = useHistory();
    const axios = useAxios();

    const resourceGroupApi = useMutation((payload) => axios.get(endPoints.mySql.resourceGroup(payload)));

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }
    async function checkCatalog() {
        const isWindows = osType === 'Windows';
        const selector = isWindows ? 'azure_windows_vm_host' : 'azure_rhel_vm_host';
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();

        setVmDataDisk({
            ...vmDataDisk,
            diskSizeInGib: data?.provisioning?.requestPayloadDefinition?.extra_vars[selector]?.os_disk_size_gb,
        });

        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem, osType, serverType, data?.images?.sku);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
                emptyCost: costs,
            }));
        } else if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = await formOrderObjFromOrderDetails(catalogServiceId, orderDetails, osType, serverType, data?.images?.sku);
            let resourceGroupApiResults = null;
            let subscriptionDetails = orderObj?.subscriptionDetails || null;
            const location = orderObj?.region;
            const rgName = orderObj?.resourceGroup || '';
            if (rgName && location) {
                try {
                    const response = await resourceGroupApi.mutateAsync({ location, rgName });
                    resourceGroupApiResults = Array.isArray(response?.data) ? response.data : null;
                    const matched = resourceGroupApiResults?.find((item) => item.subscriptionName === orderObj?.subscription);
                    subscriptionDetails = matched || resourceGroupApiResults?.[0] || null;
                } catch (error) {
                    resourceGroupApiResults = null;
                }
            }
            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                resourceGroupDetails: resourceGroupApiResults,
                subscription: subscriptionDetails?.subscriptionName || null,
                subscriptionDetails,
                emptyCost: costs,
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                osType,
                sku: data?.images?.sku,
                serverType,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }
    const reset = () => {
        setOrder({
            ...order,
            ...resetOrder,
        });
        resetOrderId('');
        resetEnvDetails(restEnv);
        resetIsMandatoryTagsFullfilled(false);
        resetToggleSspModels({
            isTagOpen: false,
            isDiskOpen: false,
            isApplicationOpen: false,
            isSubmitOrderSuccess: false,
            isCostSplitOpen: false,
            isDataDisk: { isOpen: false, newDataDiskCount: 0, deleteCount: 0 },
            isNvmeOpen: false,
        });
        resetIsConfigDiskFulfilled(false);
        resetQuickConfig(undefined);
    };
    React.useEffect(() => {
        if (cmpCatalogs?.length && osType && serverType && catalogCode) {
            // Check if user has access to this catalog, redirect if not
            const shouldRedirect = checkAndRedirectCatalog(catalogCode, history);
            if (shouldRedirect) {
                return; // Exit early if redirected
            }

            setLoading(true);
            reset();
            setVmDataDisk(undefined);
            checkCatalog();
        }
    }, [cmpCatalogs, osType, serverType, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);
    return (
        <>
            <Helmet>
                <title>Oracle VM Provisioning - DevX Infra</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <CurrentServerDetails />
                        <Tags />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ConfirmDD />
                        <SamRequest />
                        <QuickConfig />
                        <ShowCostDetails />
                        <NVMeDialogModal />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default OracleProvisioningPage;
